import { DOCUMENT } from '@angular/common';
import { inject, Injectable, InjectionToken } from '@angular/core';
import { BehaviorSubject, forkJoin, fromEvent, Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, finalize, map, shareReplay, tap } from 'rxjs/operators';
import { HttpService } from '../../../services/http/http.service';
import { UtilityService } from '../../../services/utility/utility.service';
import {
  allows,
  GAP_SERVICE_APIS,
  GapLevel,
  GapOperation,
  GapOperationLevels,
  GapOperationPermission,
  GapServiceApi,
  levelsOf,
  requiredLevel,
  toGapLevel
} from '../../permissions/gap-permissions';
import { environment } from '../../../../environments/environment';

/**
 * The user's access to GAP, as authz answers it: a level, none, or not known because authz did not answer. Not
 * knowing counts as none: nothing is offered that APIM could refuse.
 */
export type GapAccess = { status: 'granted'; level: GapLevel } | { status: 'denied' } | { status: 'unreachable' };

/** How long an answer is used before a page opening asks again. */
const MAX_AGE = 5 * 60_000;
/** Back to the tab after this long, the answer is asked again, in the background. */
const VISIBLE_AGAIN_AFTER = 60_000;

/**
 * A level to take instead of asking authz, to try the UI at each level locally: `gapLevel` in environment.local.ts,
 * which the other environments do not have. Only what the UI offers follows it; APIM still checks the real level.
 */
export const GAP_LEVEL_OVERRIDE = new InjectionToken<GapLevel | null>('GAP_LEVEL_OVERRIDE', {
  providedIn: 'root',
  factory: () => (environment as { gapLevel?: GapLevel | null }).gapLevel ?? null
});

/**
 * The user's level on GAP, asked of authz (`/applications/gap/access`) when a page opens, kept for a few minutes,
 * asked again when the user comes back to the tab or when APIM refuses a call (the level may have changed). What the
 * pages offer follows it: `can('client:POST /v1/clients')`, the `gapRequires` directive, `GapPermissionGuard`.
 */
@Injectable({ providedIn: 'root' })
export class GapAccessService {
  private readonly _access = new BehaviorSubject<GapAccess | null>(null);
  private _checkedAt = 0;
  private _asking: Observable<GapAccess> | null = null;
  private readonly _override = inject(GAP_LEVEL_OVERRIDE);
  /**
   * The level each operation requires, as each GAP API publishes it (PLAT-2251). Read with the first access, kept for
   * the session: a service's levels change only with its release. An API whose list could not be read is asked again
   * on the next refresh, and its operations are not offered meanwhile.
   */
  private _levels: GapOperationLevels = {};

  /** The access as it changes; null until authz was first asked. */
  readonly access$ = this._access.asObservable();

  constructor(
    private readonly _httpService: HttpService,
    private readonly _utilityService: UtilityService
  ) {
    if (this._override) {
      console.warn(`GAP access: ${this._override} level taken from the environment, authz not asked`);
    }
    const document = inject(DOCUMENT);
    fromEvent(document, 'visibilitychange')
      .pipe(filter(() => document.visibilityState === 'visible' && this._access.value !== null && this.age() > VISIBLE_AGAIN_AFTER))
      .subscribe(() => this.refresh().subscribe());
  }

  /** The access, as cached while it is recent and was answered; asked of authz otherwise. */
  current(): Observable<GapAccess> {
    const access = this._access.value;
    return access && access.status !== 'unreachable' && this.age() < MAX_AGE ? of(access) : this.refresh();
  }

  /** Asks authz now; a question already on its way is shared, not asked twice. */
  refresh(): Observable<GapAccess> {
    this._asking ??= forkJoin([this._override ? of<GapAccess>({ status: 'granted', level: this._override }) : this.ask(), this.loadLevels()]).pipe(
      map(([access]) => access),
      tap(access => {
        this._checkedAt = Date.now();
        this._access.next(access);
      }),
      finalize(() => (this._asking = null)),
      shareReplay(1)
    );
    return this._asking;
  }

  /** Whether the user may run this operation; not while their access is not known. */
  can(operation: GapOperation): boolean {
    return this.denial(operation) === null;
  }

  /** Why the user may not run this operation, as a hover says it; null when they may. */
  denial(operation: GapOperation): string | null {
    return denialOf(this._access.value, operation, this._levels);
  }

  /** `denial`, following the access as it changes. */
  denied$(operation: GapOperation): Observable<string | null> {
    return this.access$.pipe(
      map(access => denialOf(access, operation, this._levels)),
      distinctUntilChanged()
    );
  }

  /** The level APIM requires for this operation; null while its API's list is not known. */
  requiredLevel(operation: GapOperation): GapLevel | null {
    return requiredLevel(this._levels, operation);
  }

  /** The levels of the APIs not read yet; each that cannot be read is left out, the others kept. */
  private loadLevels(): Observable<void> {
    const missing = GAP_SERVICE_APIS.filter(api => !this._levels[api]);
    if (!missing.length) {
      return of(undefined);
    }
    return forkJoin(missing.map(api => this.readLevels(api))).pipe(
      tap(lists => (this._levels = { ...this._levels, ...Object.fromEntries(lists.filter(([, levels]) => levels !== null)) })),
      map(() => undefined)
    );
  }

  private readLevels(api: GapServiceApi): Observable<[GapServiceApi, Record<string, GapLevel> | null]> {
    return this._httpService
      .createHttpClient(this._utilityService.getApiUrl(api))
      .url('/v1/operations/permissions')
      .get<GapOperationPermission[]>()
      .pipe(
        map((permissions): [GapServiceApi, Record<string, GapLevel> | null] => [api, Array.isArray(permissions) ? levelsOf(permissions) : null]),
        catchError(() => of<[GapServiceApi, Record<string, GapLevel> | null]>([api, null]))
      );
  }

  private ask(): Observable<GapAccess> {
    return this._httpService
      .createHttpClient(this._utilityService.getApiUrl('authz'))
      .url('/v1/authorization/applications/gap/access')
      .get<{ accessLevel?: string }>()
      .pipe(
        map((response): GapAccess => {
          const level = toGapLevel(response?.accessLevel);
          return level ? { status: 'granted', level } : { status: 'denied' };
        }),
        // A 403 is authz's no; anything else is no answer, which counts as no access too
        catchError(error => of<GapAccess>(error?.status === 403 ? { status: 'denied' } : { status: 'unreachable' }))
      );
  }

  private age(): number {
    return Date.now() - this._checkedAt;
  }
}

/**
 * Why this access does not allow this operation, in a few words; null when it does. Not knowing the access, or the
 * level of the operation (its API's list could not be read), counts as not allowed: nothing is offered that APIM
 * could refuse.
 */
export function denialOf(access: GapAccess | null, operation: GapOperation, levels: GapOperationLevels): string | null {
  const required = requiredLevel(levels, operation);
  if (!access || access.status === 'unreachable' || required === null) {
    return 'Your access to GAP could not be checked';
  }
  return access.status === 'granted' && allows(access.level, required) ? null : `Requires ${required} access to GAP`;
}
